import type { ReactNode } from 'react';
import React from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import type {
  CardProps,
  ComponentRenderProps,
} from '../../../types/dynamic_views';

interface CardComponentProps extends ComponentRenderProps {
  children: ReactNode;
}

// Build CSS classes - using design system tokens for dark mode support.
// rounded-xl matches the chat-module card canon (proposal cards, report
// charts) — see the chat consistency pass.
const variantClasses = {
  default: 'bg-bg-primary border border-border-secondary rounded-xl',
  bordered: 'bg-bg-secondary border border-border-secondary rounded-xl',
  compact: 'bg-bg-primary border border-border-secondary rounded-xl',
};

const shadowClasses = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
};

const paddingClasses = {
  sm: 'p-4',
  md: 'p-4',
  lg: 'p-8',
};

export const CardComponent: React.FC<CardComponentProps> = ({
  component,
  className = '',
  children,
}) => {
  const { currentOrg } = useOrg();
  const props = component.props as CardProps;
  const {
    title,
    content,
    variant = 'default',
    shadow = 'sm',
    padding = 'md',
  } = props;

  const allClasses = [
    variantClasses[variant] || variantClasses.default,
    shadowClasses[shadow] || shadowClasses.sm,
    paddingClasses[padding] || paddingClasses.md,
    'overflow-hidden', // Ensures children don't overflow the card bounds
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={allClasses}
      data-component-id={component.id}
      data-variant={variant}
    >
      {title && (
        <div className='card-header border-b border-border-secondary pb-4 mb-4'>
          <h4 className='text-base font-semibold text-fg-primary leading-tight'>
            {title}
          </h4>
        </div>
      )}
      <div className='p-6'>
        {content && (
          <MarkdownContent
            content={content}
            className='text-sm text-fg-tertiary leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0'
            orgId={currentOrg?.id}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default CardComponent;
